import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Star, Plus, Trash2, Save, Loader2, Sparkles } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FEATURED = 8;

const resolveCover = (src) => {
  if (!src) return null;
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  if (src.startsWith('/api/')) return `${BACKEND_URL}${src}`;
  if (src.startsWith('/uploads/')) return `${BACKEND_URL}/api${src}`;
  return src;
};

/**
 * Admin tile that lets admins curate the Featured rail on /discover.
 * Shows the current pin set + a "+ Add" picker that lists available
 * series/standalone content with covers. Each pinned card is reorderable
 * (move up/down) and removable.
 */
const FeaturedRailManager = () => {
  const [items, setItems] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [editingTaglineFor, setEditingTaglineFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/discover/featured`);
      if (r.ok) {
        const data = await r.json();
        setItems(data.items || []);
      }
    } catch {} finally { setLoading(false); }
  }, []);

  const loadCandidates = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/discover/featured/candidates`);
      if (r.ok) {
        const data = await r.json();
        setCandidates(data.candidates || []);
      }
    } catch {}
  };

  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/discover/featured`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: next }),
      });
      if (!r.ok) throw new Error('save failed');
      const data = await r.json();
      setItems(data.items || []);
      toast.success(`Featured rail updated · ${(data.items || []).length} pinned`);
    } catch (e) {
      toast.error('Could not save featured rail');
    } finally { setSaving(false); }
  };

  const onAdd = (cand) => {
    if (items.find((i) => i.ref_id === cand.ref_id)) {
      toast.error('Already pinned');
      return;
    }
    if (items.length >= MAX_FEATURED) {
      toast.error(`Max ${MAX_FEATURED} items`);
      return;
    }
    save([...items, cand]);
    setShowPicker(false);
  };

  const onRemove = (refId) => {
    save(items.filter((i) => i.ref_id !== refId));
  };

  const onMove = (refId, dir) => {
    const idx = items.findIndex((i) => i.ref_id === refId);
    if (idx < 0) return;
    const next = [...items];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    save(next);
  };

  const setTagline = (refId, tagline) => {
    save(items.map((i) => (i.ref_id === refId ? { ...i, tagline } : i)));
    setEditingTaglineFor(null);
  };

  const filteredCandidates = candidates.filter((c) =>
    !pickerSearch ||
    (c.title || '').toLowerCase().includes(pickerSearch.toLowerCase()) ||
    (c.author || '').toLowerCase().includes(pickerSearch.toLowerCase())
  );

  return (
    <Card
      data-testid="featured-rail-manager"
      className="bg-gradient-to-br from-slate-800/70 to-slate-900/70 border-slate-700"
    >
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-300 flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-slate-100 font-semibold">Featured rail</h3>
            <p className="text-xs text-slate-400">
              Pin up to {MAX_FEATURED} items to the top of /discover. {items.length}/{MAX_FEATURED} used.
            </p>
          </div>
          <Button
            data-testid="featured-add-btn"
            size="sm"
            disabled={items.length >= MAX_FEATURED || saving}
            onClick={async () => {
              await loadCandidates();
              setShowPicker(true);
            }}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
          >
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>

        {loading ? (
          <div className="text-xs text-slate-400 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-4 text-center">
            No items pinned yet — Discover will use auto-rails only.
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((it, idx) => (
              <li
                key={it.ref_id}
                className="flex items-start gap-3 p-2 rounded-lg bg-slate-900/50 border border-slate-700/50"
                data-testid={`featured-item-${it.ref_id}`}
              >
                <div className="w-12 h-16 rounded overflow-hidden bg-slate-800 shrink-0">
                  {it.cover_image ? (
                    <img src={resolveCover(it.cover_image)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-slate-700" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-100 font-medium truncate">{it.title}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {it.author || '—'} · {it.type}{it.episode_count ? ` · ${it.episode_count} eps` : ''}
                  </div>
                  {editingTaglineFor === it.ref_id ? (
                    <input
                      type="text"
                      defaultValue={it.tagline || ''}
                      autoFocus
                      maxLength={140}
                      placeholder="Add a marketing tagline (140 chars max)…"
                      className="mt-1 w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-100"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') setTagline(it.ref_id, e.target.value);
                        if (e.key === 'Escape') setEditingTaglineFor(null);
                      }}
                      onBlur={(e) => setTagline(it.ref_id, e.target.value)}
                      data-testid={`featured-tagline-input-${it.ref_id}`}
                    />
                  ) : (
                    <button
                      onClick={() => setEditingTaglineFor(it.ref_id)}
                      className="mt-1 text-[11px] text-amber-300/80 hover:text-amber-200 italic"
                    >
                      {it.tagline || '+ add tagline'}
                    </button>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => onMove(it.ref_id, -1)}
                    disabled={idx === 0 || saving}
                    className="text-slate-500 hover:text-white text-xs disabled:opacity-30"
                    aria-label="Move up"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => onMove(it.ref_id, +1)}
                    disabled={idx === items.length - 1 || saving}
                    className="text-slate-500 hover:text-white text-xs disabled:opacity-30"
                    aria-label="Move down"
                  >
                    ▼
                  </button>
                </div>
                <button
                  onClick={() => onRemove(it.ref_id)}
                  className="text-rose-400 hover:text-rose-300 self-center"
                  aria-label="Remove"
                  data-testid={`featured-remove-${it.ref_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Picker modal */}
        {showPicker && (
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowPicker(false)}
            data-testid="featured-picker-modal"
          >
            <div
              className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-slate-700 flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400" />
                <h4 className="text-slate-100 font-semibold">Pick something to feature</h4>
                <button
                  onClick={() => setShowPicker(false)}
                  className="ml-auto text-slate-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              <input
                type="text"
                placeholder="Search by title or author…"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                className="m-4 mt-3 mb-0 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
                data-testid="featured-picker-search"
              />
              <div className="overflow-y-auto p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredCandidates.length === 0 ? (
                  <div className="col-span-full text-sm text-slate-500 text-center py-8">
                    No items with covers found. Upload a series with a cover image first.
                  </div>
                ) : (
                  filteredCandidates.map((c) => (
                    <button
                      key={c.ref_id}
                      onClick={() => onAdd(c)}
                      className="text-left bg-slate-800/50 hover:bg-slate-700 rounded-lg overflow-hidden border border-slate-700 hover:border-amber-400 transition-colors"
                      data-testid={`featured-pick-${c.ref_id}`}
                    >
                      <div className="aspect-[3/4] bg-slate-700 overflow-hidden">
                        <img src={resolveCover(c.cover_image)} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-2">
                        <div className="text-xs text-slate-100 font-medium line-clamp-1">{c.title}</div>
                        <div className="text-[10px] text-slate-500 line-clamp-1">
                          {c.author || '—'} · {c.type}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {saving && (
          <div className="mt-3 text-xs text-slate-400 flex items-center gap-2">
            <Save className="w-3 h-3 animate-pulse" /> Saving…
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FeaturedRailManager;
